'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import EngagementPathwayCard from './EngagementPathwayCard';
import OnboardingQuiz from './OnboardingQuiz';
import MembershipTierCard from './MembershipTierCard';

import PartnershipInquiryForm from './PartnershipInquiryForm';
import VolunteerOpportunityCard from './VolunteerOpportunityCard';
import ProgressTracker from './ProgressTracker';
import RecognitionBadge from './RecognitionBadge';
import ReferralSystem from './ReferralSystem';
import MembershipRegistrationModal from './MembershipRegistrationModal';
import VolunteerApplicationModal from './VolunteerApplicationModal';
import AmbassadorApplicationModal from './AmbassadorApplicationModal';
import { membershipService } from '@/lib/services/membershipService';
import { volunteerService } from '@/lib/services/volunteerService';
import Link from 'next/link';
import DailyExperienceSection from '@/app/festival-guide/components/DailyExperienceSection';
import DayTwoExperienceSection from '@/app/festival-guide/components/DayTwoExperienceSection';
import DayExperienceSection from '@/app/festival-guide/components/DayExperienceSection';
import MovementRegistrationPopup from '@/components/common/MovementRegistrationPopup';

interface EngagementPathway {
  id: string;
  title: string;
  description: string;
  iconName: string;
  benefits: string[];
  ctaText: string;
}

interface MembershipTier {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  responsibilities: string[];
  isPopular?: boolean;
}

interface VolunteerOpportunity {
  id: number;
  title: string;
  location: string;
  timeCommitment: string;
  skills: string[];
  description: string;
  image: string;
  alt: string;
}

interface Milestone {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  date?: string;
}

interface Badge {
  id: number;
  name: string;
  description: string;
  iconName: string;
  earned: boolean;
  earnedDate?: string;
  progress?: number;
}

const JoinTheMovementInteractive: React.FC = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedPathway, setSelectedPathway] = useState<string | null>(null);
  const [showAmbassadorForm, setShowAmbassadorForm] = useState(false);
  const [showPartnershipForm, setShowPartnershipForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'pathways' | 'membership' | 'volunteer' | 'partnerships' | 'daily-practice'>(
    'pathways'
  );
  const [selectedMembershipTier, setSelectedMembershipTier] = useState<MembershipTier | null>(null);
  const [selectedVolunteerOpportunity, setSelectedVolunteerOpportunity] = useState<VolunteerOpportunity | null>(null);
  const [showRegistrationPopup, setShowRegistrationPopup] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const engagementPathways: EngagementPathway[] = [
  {
    id: 'individual',
    title: 'Individual Journey',
    description: 'Begin your personal transformation with daily practices and reflection',
    iconName: 'UserIcon',
    benefits: [
    'Access to 30-day preparation program',
    'Daily meditation and reflection prompts',
    'Personal progress tracking dashboard',
    'Community support network',
    'Monthly virtual gatherings'],

    ctaText: 'Start Your Journey'
  },
  {
    id: 'organizer',
    title: 'Community Organizer',
    description: 'Lead local chapters and coordinate peace-building events',
    iconName: 'UsersIcon',
    benefits: [
    'Complete organizing toolkit',
    'Event planning resources',
    'Marketing materials and templates',
    'Direct founder mentorship',
    'Regional coordinator network'],

    ctaText: 'Become an Organizer'
  },
  {
    id: 'supporter',
    title: 'Humanitarian Supporter',
    description: 'Directly support vulnerable populations through targeted giving',
    iconName: 'HeartIcon',
    benefits: [
    'Choose specific support areas',
    'Transparent impact reporting',
    'Quarterly beneficiary updates',
    'Tax-deductible contributions',
    'Exclusive donor recognition'],

    ctaText: 'Support the Cause'
  },
  {
    id: 'advocate',
    title: 'Policy Advocate',
    description: 'Champion official recognition and systemic peace-building',
    iconName: 'MegaphoneIcon',
    benefits: [
    'Advocacy campaign toolkits',
    'Government outreach templates',
    'Research and evidence library',
    'Policy briefing sessions',
    'Academic collaboration opportunities'],

    ctaText: 'Join Advocacy'
  }];


  const membershipTiers: MembershipTier[] = [
  {
    id: 'peace-seeker',
    name: 'Peace Seeker',
    price: 'Free',
    description: 'Begin your journey with essential resources',
    features: [
    'Access to basic preparation materials',
    'Monthly newsletter',
    'Community forum access',
    'April 17th celebration guide'],

    responsibilities: [
    'Complete personal reflection exercises',
    'Participate in online discussions',
    'Share your transformation story']

  },
  {
    id: 'peace-builder',
    name: 'Peace Builder',
    price: '$10',
    description: 'Deepen your commitment with enhanced support',
    features: [
    'All Peace Seeker benefits',
    'Weekly guided meditations',
    'Advanced forgiveness frameworks',
    'Priority event registration',
    'Exclusive webinar access'],

    responsibilities: [
    'Attend monthly virtual gatherings',
    'Mentor new community members',
    'Contribute to resource library'],

    isPopular: true
  },
  {
    id: 'peace-champion',
    name: 'Peace Champion',
    price: '$50',
    description: 'Lead the movement with comprehensive resources',
    features: [
    'All Peace Builder benefits',
    'One-on-one coaching sessions',
    'Ambassador certification program',
    'Co-branding opportunities',
    'Direct founder access'],

    responsibilities: [
    'Organize local chapter events',
    'Recruit and train volunteers',
    'Report quarterly impact metrics']

  }];


  const volunteerOpportunities: VolunteerOpportunity[] = [
  {
    id: 1,
    title: 'Social Media Ambassador',
    location: 'Remote',
    timeCommitment: '5-10 hours/week',
    skills: ['Social Media', 'Content Creation', 'Graphic Design'],
    description:
    'Create engaging content and manage social media presence to spread SIG Day (The Global No Enemy Festival) message across digital platforms.',
    image: "https://images.unsplash.com/photo-1676275773814-ad0819d45436",
    alt: 'Person working on laptop with social media icons displayed on screen in modern office'
  },
  {
    id: 2,
    title: 'Event Coordinator',
    location: 'New York, USA',
    timeCommitment: '10-15 hours/week',
    skills: ['Event Planning', 'Project Management', 'Communication'],
    description:
    'Plan and execute local SIG Day (The Global No Enemy Festival) celebrations and community gatherings to bring people together in peace.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9c5f58a-1767536392081.png",
    alt: 'Group of diverse people collaborating around table with event planning materials'
  },
  {
    id: 3,
    title: 'Translation Volunteer',
    location: 'Remote',
    timeCommitment: '3-5 hours/week',
    skills: ['Translation', 'Writing', 'Cultural Sensitivity'],
    description:
    'Translate peace-building resources and materials to make the movement accessible to global communities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4f32171-1766911826734.png",
    alt: 'Multilingual books and translation documents spread across wooden desk'
  },
  {
    id: 4,
    title: 'Community Outreach Specialist',
    location: 'London, UK',
    timeCommitment: '8-12 hours/week',
    skills: ['Public Speaking', 'Networking', 'Community Organizing'],
    description:
    'Build relationships with local organizations and faith communities to expand the movement\'s reach.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b9a8d80a-1767424368660.png",
    alt: 'Community leader speaking to engaged audience at outdoor gathering'
  }];


  const mockMilestones: Milestone[] = [
  {
    id: 1,
    title: 'Welcome to SIG Day',
    description: 'Complete your profile and set your intentions for The Global No Enemy Festival',
    completed: true,
    date: 'January 15, 2026'
  },
  {
    id: 2,
    title: 'First Week Complete',
    description: 'Finish your first 7 days of preparation',
    completed: true,
    date: 'January 22, 2026'
  },
  {
    id: 3,
    title: 'Community Connection',
    description: 'Join a local chapter or virtual gathering',
    completed: false
  },
  {
    id: 4,
    title: 'Share Your Story',
    description: 'Post your transformation journey',
    completed: false
  },
  {
    id: 5,
    title: 'April 17th Pledge',
    description: 'Commit to celebrating SIG Day (The Global No Enemy Festival) 2026',
    completed: false
  }];


  const mockBadges: Badge[] = [
  {
    id: 1,
    name: 'First Steps',
    description: 'Completed your first week of preparation',
    iconName: 'SparklesIcon',
    earned: true,
    earnedDate: 'January 22, 2026'
  },
  {
    id: 2,
    name: 'Community Builder',
    description: 'Invited 5 people to join the movement',
    iconName: 'UserGroupIcon',
    earned: true,
    earnedDate: 'January 25, 2026'
  },
  {
    id: 3,
    name: 'Peace Advocate',
    description: 'Signed 3 advocacy petitions',
    iconName: 'MegaphoneIcon',
    earned: false,
    progress: 66
  },
  {
    id: 4,
    name: 'Generous Heart',
    description: 'Made your first humanitarian donation',
    iconName: 'HeartIcon',
    earned: false,
    progress: 0
  }];


  const handlePathwaySelect = (pathwayId: string) => {
    setSelectedPathway(pathwayId);
    if (pathwayId === 'individual') {
      router.push('/festival-guide');
    } else if (pathwayId === 'supporter') {
      router.push('/donate');
    } else if (pathwayId === 'organizer') {
      setShowAmbassadorForm(true);
    } else if (pathwayId === 'advocate') {
      setShowPartnershipForm(true);
    }
  };

  const handleQuizComplete = (pathway: string) => {
    setSelectedPathway(pathway);
    setShowQuiz(false);
  };

  const handleAmbassadorSubmit = async () => {
    setShowAmbassadorForm(false);
  };

  const handlePartnershipSubmit = (data: any) => {
    // Validate and process partnership inquiry
    if (data.organizationName && data.email) {
      alert(`Partnership Inquiry Submitted!\n\nThank you for your interest in partnering with SIG Day!\n\nOrganization: ${data.organizationName}\nContact: ${data.contactName}\n\nOur partnerships team will review your inquiry and reach out to ${data.email} within 3-5 business days to discuss collaboration opportunities.\n\nTogether, we can amplify the message of peace and forgiveness!`);
      setShowPartnershipForm(false);
      // In production, this would send data to backend API
    }
  };

  const handleVolunteerApply = (opportunityId: number) => {
    const opportunity = volunteerOpportunities.find((opp) => opp.id === opportunityId);
    if (opportunity) {
      setSelectedVolunteerOpportunity(opportunity);
    }
  };

  const handleMembershipSelect = (tierId: string) => {
    const tier = membershipTiers.find((t) => t.id === tierId);
    if (tier) {
      if (tier.id === 'supporter') {
        router.push('/donate');
      } else {
        setSelectedMembershipTier(tier);
      }
    }
  };

  const handleMembershipSubmit = async (data: {
    fullName: string;
    email: string;
    phone?: string;
    tierId: string;
    tierName: string;
    tierPrice: string;
    message?: string;
  }) => {
    try {
      const result = await membershipService.register(data);
      
      if (result) {
        alert(`Membership Registration Successful!\n\nThank you, ${data.fullName}!\n\nYou've successfully registered as a ${data.tierName}.\n\nA confirmation email has been sent to ${data.email}.\n\nWelcome to the global peace movement!`);
      } else {
        alert('Registration submitted successfully! We\'ll contact you soon.');
      }
      
      setSelectedMembershipTier(null);
    } catch (error: any) {
      console.error('Membership registration error:', error);
      alert('There was an error submitting your registration. Please try again.');
    }
  };

  const handleVolunteerSubmit = async (data: {
    fullName: string;
    email: string;
    phone?: string;
    opportunityId: number;
    opportunityTitle: string;
    location: string;
    skills: string[];
    motivation?: string;
    availability?: string;
  }) => {
    try {
      const result = await volunteerService.signUp({
        opportunityId: data.opportunityId,
        opportunityTitle: data.opportunityTitle,
        location: data.location,
        volunteerName: data.fullName,
        volunteerEmail: data.email,
        volunteerPhone: data.phone,
        skills: data.skills,
        availability: data.availability,
        motivation: data.motivation,
      });
      
      if (result) {
        alert(`Volunteer Application Submitted!\n\nThank you, ${data.fullName}!\n\nYour application for ${data.opportunityTitle} has been received.\n\nOur volunteer coordinator will review your application and contact you at ${data.email} within 3-5 business days.\n\nThank you for your commitment to peace!`);
      } else {
        alert('Application submitted successfully! We\'ll contact you soon.');
      }
      
      setSelectedVolunteerOpportunity(null);
    } catch (error: any) {
      console.error('Volunteer application error:', error);
      alert('There was an error submitting your application. Please try again.');
    }
  };

  if (!isHydrated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-20" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground mb-6">
              Join the Global Peace Movement
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Choose your path to transformation. Every journey begins with a single step toward
              peace, forgiveness, and healing.
            </p>
            <button
              onClick={() => setShowQuiz(true)}
              className="px-8 py-4 bg-accent text-accent-foreground rounded-md font-cta text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg pulse-gentle">

              Find Your Path
            </button>
          </div>
        </div>
      </section>

      {/* Quiz Modal */}
      {showQuiz &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="relative max-w-4xl w-full">
            <button
            onClick={() => setShowQuiz(false)}
            className="absolute -top-4 -right-4 w-10 h-10 bg-card rounded-full flex items-center justify-center shadow-lg hover:bg-muted transition-colors z-10"
            aria-label="Close quiz">

              <Icon name="XMarkIcon" size={24} className="text-foreground" />
            </button>
            <OnboardingQuiz onComplete={handleQuizComplete} />
          </div>
        </div>
      }

      {/* Ambassador Form Modal */}
      {showAmbassadorForm && (
        <AmbassadorApplicationModal
          onSubmit={handleAmbassadorSubmit}
          onCancel={() => setShowAmbassadorForm(false)}
        />
      )}

      {/* Partnership Form Modal */}
      {showPartnershipForm &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
          <PartnershipInquiryForm
          onSubmit={handlePartnershipSubmit}
          onCancel={() => setShowPartnershipForm(false)} />

        </div>
      }

      {/* Membership Registration Modal */}
      {selectedMembershipTier && (
        <MembershipRegistrationModal
          tierName={selectedMembershipTier.name}
          tierId={selectedMembershipTier.id}
          tierPrice={selectedMembershipTier.price}
          onSubmit={handleMembershipSubmit}
          onCancel={() => setSelectedMembershipTier(null)}
        />
      )}

      {/* Volunteer Application Modal */}
      {selectedVolunteerOpportunity && (
        <VolunteerApplicationModal
          opportunityId={selectedVolunteerOpportunity.id}
          opportunityTitle={selectedVolunteerOpportunity.title}
          location={selectedVolunteerOpportunity.location}
          timeCommitment={selectedVolunteerOpportunity.timeCommitment}
          requiredSkills={selectedVolunteerOpportunity.skills}
          onSubmit={handleVolunteerSubmit}
          onCancel={() => setSelectedVolunteerOpportunity(null)}
        />
      )}

      {/* Navigation Tabs */}
      <section className="bg-card border-b border-border sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center space-x-1 overflow-x-auto">
            {[
            { id: 'pathways', label: 'Engagement Pathways', icon: 'MapIcon' },
            { id: 'membership', label: 'Membership Tiers', icon: 'StarIcon' },
            { id: 'volunteer', label: 'Volunteer Opportunities', icon: 'HandRaisedIcon' },
            { id: 'daily-practice', label: 'Daily Practice', icon: 'SparklesIcon' },
            { id: 'progress', label: 'Your Progress', icon: 'ChartBarIcon' }].
            map((tab) =>
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${
              activeTab === tab.id ?
              'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
              }>

                <Icon name={tab.icon as any} size={20} />
                <span>{tab.label}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Engagement Pathways */}
      {activeTab === 'pathways' &&
      <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
                Choose Your Engagement Level
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Every contribution matters. Select the pathway that resonates with your capacity
                and calling.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {engagementPathways.map((pathway) =>
            <EngagementPathwayCard
              key={pathway.id}
              title={pathway.title}
              description={pathway.description}
              iconName={pathway.iconName}
              benefits={pathway.benefits}
              ctaText={pathway.ctaText}
              onSelect={() => handlePathwaySelect(pathway.id)} />

            )}
            </div>
          </div>
        </section>
      }

      {/* Membership Tiers */}
      {activeTab === 'membership' &&
      <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
                Membership Tiers
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Deepen your commitment with structured support and clear responsibilities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {membershipTiers.map((tier) =>
            <MembershipTierCard
              key={tier.id}
              name={tier.name}
              price={tier.price}
              description={tier.description}
              features={tier.features}
              responsibilities={tier.responsibilities}
              isPopular={tier.isPopular}
              onSelect={() => handleMembershipSelect(tier.id)} />

            )}
            </div>
          </div>
        </section>
      }

      {/* Volunteer Opportunities */}
      {activeTab === 'volunteer' &&
      <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
                Volunteer Opportunities
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Use your skills to advance the movement. Find opportunities matched to your
                location and interests.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {volunteerOpportunities.map((opportunity) =>
            <VolunteerOpportunityCard
              key={opportunity.id}
              title={opportunity.title}
              location={opportunity.location}
              timeCommitment={opportunity.timeCommitment}
              skills={opportunity.skills}
              description={opportunity.description}
              image={opportunity.image}
              alt={opportunity.alt}
              onApply={() => handleVolunteerApply(opportunity.id)} />

            )}
            </div>
          </div>
        </section>
      }

      {/* Daily Practice */}
      {activeTab === 'daily-practice' && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
                Daily Practice
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Engage with daily activities and rituals to deepen your transformation journey. Record your responses and track your progress alongside the global community.
              </p>
            </div>
            <DailyExperienceSection />
            <DayTwoExperienceSection />
            {Array.from({ length: 28 }, (_, i) => i + 3).map((day) => (
              <DayExperienceSection key={day} dayNumber={day} />
            ))}
          </div>
        </section>
      )}

      {/* Progress Tracking */}
      {activeTab === 'progress' &&
      <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
                Your Transformation Journey
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Track your progress, celebrate milestones, and see your impact grow.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-2">
                <ProgressTracker
                milestones={mockMilestones}
                currentLevel="Peace Builder"
                totalPoints={1250} />

              </div>
              <div>
                <ReferralSystem
                referralCode="PEACE2026"
                referralCount={12}
                referralRewards={[
                { milestone: 5, reward: 'Community Builder Badge', achieved: true },
                { milestone: 10, reward: 'Peace Ambassador Certificate', achieved: true },
                { milestone: 25, reward: 'Founder Recognition', achieved: false },
                { milestone: 50, reward: 'Global Impact Award', achieved: false }]
                } />

              </div>
            </div>

            <div>
              <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
                Recognition Badges
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {mockBadges.map((badge) =>
              <RecognitionBadge
                key={badge.id}
                name={badge.name}
                description={badge.description}
                iconName={badge.iconName}
                earned={badge.earned}
                earnedDate={badge.earnedDate}
                progress={badge.progress} />

              )}
              </div>
            </div>
          </div>
        </section>
      }

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary to-secondary py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-white mb-6">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Join thousands of peace-seekers worldwide in choosing compassion over hostility.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => setShowRegistrationPopup(true)}
              className="px-8 py-4 bg-white text-primary rounded-lg font-cta text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl inline-flex items-center space-x-2">
              <Icon name="CalendarIcon" size={20} />
              <span>Start 30-Day Journey</span>
            </button>
            <Link
              href="/humanitarian-action#volunteer-opportunities"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-cta text-lg font-bold transition-all duration-300 hover:bg-white hover:text-primary inline-flex items-center space-x-2">
              
              <Icon name="HandRaisedIcon" size={20} />
              <span>Volunteer Opportunities</span>
            </Link>
          </div>
        </div>
      </section>

      {showRegistrationPopup && (
        <MovementRegistrationPopup
          sourcePage="join-the-movement-cta"
          isOpen={showRegistrationPopup}
          onClose={() => setShowRegistrationPopup(false)}
        />
      )}
    </div>);

};

export default JoinTheMovementInteractive;